import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Login from './components/Login';
import Rankings from './pages/Rankings';
import Players from './pages/Players';
import Teams from './pages/Teams';
import AdminDashboard from './pages/AdminDashboard';
import Evaluations from './pages/Evaluations';
import EvaluationHistory from './pages/EvaluationHistory';
import PlayerDashboard from './pages/PlayerDashboard';
import PlayerProfile from './pages/PlayerProfile';
import AdminPlayerEvaluations from './pages/AdminPlayerEvaluations';
import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { token, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }
  
  return token ? children : <Navigate to="/login" />;
};

const AppRoutes: React.FC = () => {
  const { token, user, isAdmin } = useAuth();
  
  // Determine the default landing page based on user role
  const getDefaultRoute = () => {
    if (!user) return '/login';
    // Coaches should go to admin view
    if (user.player?.position === 'Coach') {
      return '/';
    }
    if (user.role === 'player' || user.role === 'guardian') {
      return '/player/dashboard';
    }
    return '/';
  };
  
  return (
    <>
      {token && <Navbar />}
      <Routes>
        <Route path="/login" element={token ? <Navigate to={getDefaultRoute()} /> : <Login />} />
        
        {/* Player/Guardian Routes */}
        <Route path="/player/dashboard" element={<PrivateRoute><PlayerDashboard /></PrivateRoute>} />
        <Route path="/player/profile" element={<PrivateRoute><PlayerProfile /></PrivateRoute>} />
        
        {/* Admin-only Routes */}
        <Route path="/admin/player/:playerId/evaluations" element={<PrivateRoute>{isAdmin ? <AdminPlayerEvaluations /> : <Navigate to="/player/dashboard" />}</PrivateRoute>} />
        
        {/* Shared Routes - Available to all authenticated users */}
        <Route path="/" element={<PrivateRoute>{isAdmin ? <Rankings /> : <Navigate to="/player/dashboard" />}</PrivateRoute>} />
        <Route path="/rankings" element={<PrivateRoute><Rankings /></PrivateRoute>} />
        <Route path="/players" element={<PrivateRoute><Players /></PrivateRoute>} />
        <Route path="/teams" element={<PrivateRoute><Teams /></PrivateRoute>} />
        <Route path="/evaluations" element={<PrivateRoute><Evaluations /></PrivateRoute>} />
        <Route path="/evaluation-history" element={<PrivateRoute><EvaluationHistory /></PrivateRoute>} />
        <Route path="/admin" element={<PrivateRoute>{isAdmin ? <AdminDashboard /> : <Navigate to="/player/dashboard" />}</PrivateRoute>} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
